import React, { useState, useEffect } from "react";
import "./SidekickCard.css";
import { Accordion } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faGamepad,
    faCode,
    faBatteryFull,
    faBatteryThreeQuarters,
    faBatteryHalf,
    faBatteryQuarter,
    faBatteryEmpty,
    faCircle
} from "@fortawesome/free-solid-svg-icons";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { useTimezone } from "../../../../contexts/TimezoneContext";
import { getBondColor } from "../../../../../../lib/ui/colorUtils";
import { padDeviceId } from "../../../../../../lib/utils";
import ReactJson from "react-json-view";

function SidekickCard({ sidekicks, bondsData }) {
    const { formatDate, getRelativeTime } = useTimezone();
    const [renderedSidekicks, setRenderedSidekicks] = useState([]);

    useEffect(() => {
        renderSidekicks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sidekicks, bondsData, formatDate, getRelativeTime]);

    const getDeviceName = (bondId, deviceId) => {
        if (!bondsData || !bondsData[bondId]) {
            return deviceId; // Return device ID if bond data not available
        }

        const bondData = bondsData[bondId];
        if (!bondData.devices) {
            return deviceId;
        }

        // Handle both array and object formats
        const devicesList = Array.isArray(bondData.devices)
            ? bondData.devices
            : Object.values(bondData.devices);

        // Pad both IDs for comparison
        const paddedSearchId = padDeviceId(deviceId);
        const device = devicesList.find(d => {
            const dId = d.device_id || d.id || d._id;
            const paddedDId = padDeviceId(dId);
            return dId === deviceId || dId === paddedSearchId || paddedDId === deviceId || paddedDId === paddedSearchId;
        });
        return device?.name || deviceId;
    };

    const renderSidekicks = () => {
        if (!sidekicks || sidekicks.length === 0) {
            setRenderedSidekicks([]);
            return;
        }

        const rendered = sidekicks.map((sidekick, index) => {
            const data = sidekick.data || {};
            return (
                <Accordion.Item eventKey={`sidekick-${index}`} key={sidekick.sidekick_id || index}>
                    <Accordion.Header>
                        <div className="d-flex align-items-center w-100" style={{ gap: '0.5rem' }}>
                            {getBatteryBadge(data.battery)}
                            <span className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                                {data.name || sidekick.sidekick_id}
                            </span>
                            <div className="d-flex flex-shrink-0" style={{ gap: '0.25rem' }}>
                                {sidekick.bond_ids && sidekick.bond_ids.map((bondId, idx) => (
                                    <Badge key={idx} className="bondBadge" style={{'--bond-badge-bg': getBondColor(bondId)}}>
                                        {bondId}
                                    </Badge>
                                ))}
                                {data.model && (
                                    <Badge bg="dark">{data.model}</Badge>
                                )}
                            </div>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body className="p-3">
                        <div className="d-flex gap-2 mb-2">
                            {data.name && (
                                <FloatingLabel label="Name" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.name} />
                                </FloatingLabel>
                            )}
                            {sidekick.sidekick_id && (
                                <FloatingLabel label="Bond ID" className="flex-fill">
                                    <Form.Control readOnly={true} value={sidekick.sidekick_id} />
                                </FloatingLabel>
                            )}
                            {data.location && (
                                <FloatingLabel label="Location" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.location} />
                                </FloatingLabel>
                            )}
                            {data.type && (
                                <FloatingLabel label="Type" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.type} />
                                </FloatingLabel>
                            )}
                        </div>

                        <div className="d-flex gap-2 mb-2">
                            {data.model && (
                                <FloatingLabel label="Model" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.model} />
                                </FloatingLabel>
                            )}
                            {data.keys !== undefined && (
                                <FloatingLabel label="Keys" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.keys} />
                                </FloatingLabel>
                            )}
                            {data.chans !== undefined && (
                                <FloatingLabel label="Channels" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.chans} />
                                </FloatingLabel>
                            )}
                        </div>

                        <div className="d-flex gap-2 mb-2">
                            {data.battery !== undefined && (
                                <FloatingLabel label="Battery" className="flex-fill">
                                    <Form.Control readOnly={true} value={`${data.battery}%`} />
                                </FloatingLabel>
                            )}
                            {data.signal !== undefined && (
                                <FloatingLabel label="Signal" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.signal} />
                                </FloatingLabel>
                            )}
                            {data.fw_ver && (
                                <FloatingLabel label="Firmware Version" className="flex-fill">
                                    <Form.Control readOnly={true} value={data.fw_ver} />
                                </FloatingLabel>
                            )}
                        </div>

                        {sidekick.created && (
                            <div className="d-flex gap-2 mb-2">
                                <FloatingLabel label={`Created ${getRelativeTime(sidekick.created)}`} className="flex-fill">
                                    <Form.Control readOnly={true} value={formatDate(sidekick.created)} />
                                </FloatingLabel>
                                {sidekick.modified && (
                                    <FloatingLabel label={`Modified ${getRelativeTime(sidekick.modified)}`} className="flex-fill">
                                        <Form.Control readOnly={true} value={formatDate(sidekick.modified)} />
                                    </FloatingLabel>
                                )}
                            </div>
                        )}

                        {/* Channels Section */}
                        {data.chans > 0 && (
                            <Accordion defaultActiveKey="" className="mb-2">
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>
                                        <div className="justify-content-between w-100">
                                            <div className="float-start">
                                                <FontAwesomeIcon icon={faCircle} className="me-2" />
                                                Channels
                                            </div>
                                            <Badge bg="info" className="float-end">
                                                {data.chans}
                                            </Badge>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body className="p-3">
                                        <Accordion>
                                            {Array.from({ length: data.chans }, (_, i) => i + 1).map(channelNum => {
                                                const techValue = data.techs ? data.techs[channelNum - 1] : undefined;
                                                const chanLinks = data.chan_links ? data.chan_links[channelNum.toString()] : [];
                                                const hasData = techValue !== undefined || (chanLinks && chanLinks.length > 0);

                                                return (
                                                    <Accordion.Item eventKey={`channel-${channelNum}`} key={channelNum}>
                                                        <Accordion.Header>
                                                            <div className="d-flex flex-wrap align-items-center w-100" style={{gap: '0.5rem'}}>
                                                                <Badge bg="primary" className="flex-shrink-0">
                                                                    Ch {channelNum}
                                                                </Badge>
                                                                {techValue !== undefined && techValue !== 0 && (
                                                                    <Badge bg="secondary" className="flex-shrink-0">
                                                                        Tech: {techValue}
                                                                    </Badge>
                                                                )}
                                                                {chanLinks && chanLinks.length > 0 && chanLinks.map((link, linkIdx) => {
                                                                    // Find device name from bond data
                                                                    let deviceName = link.device;
                                                                    if (sidekick.bond_ids) {
                                                                        for (const bondId of sidekick.bond_ids) {
                                                                            const name = getDeviceName(bondId, link.device);
                                                                            if (name !== link.device) {
                                                                                deviceName = name;
                                                                                break;
                                                                            }
                                                                        }
                                                                    }
                                                                    return (
                                                                        <Badge key={linkIdx} bg="success" className="flex-shrink-0">
                                                                            {deviceName}
                                                                        </Badge>
                                                                    );
                                                                })}
                                                                {!hasData && (
                                                                    <Badge bg="info" className="flex-shrink-0">(empty)</Badge>
                                                                )}
                                                            </div>
                                                        </Accordion.Header>
                                                        <Accordion.Body className="p-3">
                                                            {techValue !== undefined && (
                                                                <FloatingLabel label="Tech Value" className="mb-2">
                                                                    <Form.Control readOnly={true} value={techValue} />
                                                                </FloatingLabel>
                                                            )}

                                                            {/* Channel Links */}
                                                            {chanLinks && chanLinks.length > 0 && (
                                                                <div className="mt-3">
                                                                    <h6>Channel Links</h6>
                                                                    {chanLinks.map((link, linkIdx) => {
                                                                        // Find which bond this link belongs to
                                                                        let deviceName = link.device;
                                                                        if (sidekick.bond_ids) {
                                                                            for (const bondId of sidekick.bond_ids) {
                                                                                const name = getDeviceName(bondId, link.device);
                                                                                if (name !== link.device) {
                                                                                    deviceName = name;
                                                                                    break;
                                                                                }
                                                                            }
                                                                        }

                                                                        return (
                                                                            <div key={linkIdx} className="mb-2">
                                                                                <div className="d-flex gap-2">
                                                                                    <FloatingLabel label="Device Name" className="flex-fill">
                                                                                        <Form.Control readOnly={true} value={deviceName} />
                                                                                    </FloatingLabel>
                                                                                    <FloatingLabel label="Device ID" className="flex-fill">
                                                                                        <Form.Control readOnly={true} value={link.device} />
                                                                                    </FloatingLabel>
                                                                                    {link.direct !== undefined && (
                                                                                        <FloatingLabel label="Direct" className="flex-fill">
                                                                                            <Form.Control readOnly={true} value={link.direct ? 'Yes' : 'No'} />
                                                                                        </FloatingLabel>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                );
                                            })}
                                        </Accordion>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        )}

                        {/* Bridges */}
                        {sidekick.bond_ids && sidekick.bond_ids.length > 0 && (
                            <div className="mb-3">
                                <h6>Bridges</h6>
                                <div className="d-flex flex-wrap gap-1">
                                    {sidekick.bond_ids.map((bondId, idx) => (
                                        <Badge key={idx} style={{'--bond-badge-bg': getBondColor(bondId)}}>
                                            {bondId}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Raw JSON */}
                        <div className="mt-3">
                            <Accordion>
                                <Accordion.Item eventKey="json">
                                    <Accordion.Header>
                                        <FontAwesomeIcon icon={faCode} className="me-2" />
                                        Raw JSON
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <ReactJson
                                            src={sidekick.raw_sidekicks || [sidekick]}
                                            theme="ocean"
                                            collapsed={1}
                                            displayDataTypes={false}
                                            displayObjectSize={false}
                                            enableClipboard={true}
                                            name={false}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            );
        });

        setRenderedSidekicks(rendered);
    };

    const getBatteryBadge = (battery) => {
        if (battery === undefined) return null;

        let icon, color;
        if (battery > 75) {
            icon = faBatteryFull;
            color = "success";
        } else if (battery > 50) {
            icon = faBatteryThreeQuarters;
            color = "success";
        } else if (battery > 25) {
            icon = faBatteryHalf;
            color = "warning";
        } else if (battery > 10) {
            icon = faBatteryQuarter;
            color = "danger";
        } else {
            icon = faBatteryEmpty;
            color = "danger";
        }

        return (
            <Badge bg={color} className="flex-shrink-0">
                <FontAwesomeIcon icon={icon} className="" />
                {battery}%
            </Badge>
        );
    };

    if (!sidekicks || sidekicks.length === 0) {
        return null;
    }

    return (
        <Accordion defaultActiveKey="" className="mb-2">
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                            <FontAwesomeIcon icon={faGamepad} className="me-2" />
                            Sidekicks
                        </div>
                        <Badge bg="dark">
                            {sidekicks.length}
                        </Badge>
                    </div>
                </Accordion.Header>
                <Accordion.Body className="p-2">
                    <Accordion>
                        {renderedSidekicks}
                    </Accordion>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default SidekickCard;
